import {
  addMonths,
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";

import { Timeframe } from "../types/timeframe";

/**
 * Generates a time range based on the selected period.
 * @param timeframe - '6m' for last 6 months, 'm' for last month, 'w' for last week. All include today.
 * @returns An object with 'from' and 'to' Date values.
 */
export function generateTimeRange(timeframe: Timeframe): {
  from: Date;
  to: Date;
} {
  const to = endOfDay(new Date()); // Today, at start of day
  let from: Date;

  switch (timeframe) {
    case "6m":
      // From 6 months ago (start of day) to today (inclusive)
      from = startOfDay(subMonths(to, 6));
      break;
    case "m":
      // From 1 month ago (start of day) to today (inclusive)
      from = startOfDay(subMonths(to, 1));
      break;
    case "w":
      // From 1 week ago (start of day) to today (inclusive)
      from = startOfDay(subDays(to, 6));
      break;
    default:
      from = to;
  }

  return { from, to };
}

/**
 * Generates a time range from the center of the selected period.
 * @param timeframe - '6m' for last 3 months and future 3 months, 'm' for this month and 'w' for this week. All include today.
 * @returns An object with 'from' and 'to' Date values.
 */
export function generateTimeRangeFromCenter(timeframe: Timeframe): {
  from: Date;
  to: Date;
} {
  const today = startOfDay(new Date());
  let from: Date;
  let to: Date;
  let dayOfWeek: number;
  let mondayOffset: number;

  switch (timeframe) {
    case "6m":
      // 3 months before and 3 months after today
      from = startOfDay(subMonths(today, 3));
      to = startOfDay(addMonths(today, 3));
      break;
    case "m":
      // From start of current month to end of current month
      from = startOfMonth(today);
      to = endOfMonth(today);
      break;
    case "w":
      // From start of current week (Monday) to end of current week (Sunday)
      dayOfWeek = today.getDay();
      // Convert Sunday (0) to 6, and shift other days back by 1
      mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      from = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - mondayOffset,
      );
      to = new Date(from);
      to.setDate(from.getDate() + 6);
      break;
    default:
      from = to = today;
  }

  return { from, to };
}
